import React, { useState, useEffect } from 'react';
import { useParams } from 'react-router-dom';
import { doc, getDoc, setDoc, updateDoc, arrayUnion } from 'firebase/firestore';
import { db, isDemoMode } from '../firebase/config';
import { FiChevronRight, FiBookmark, FiCheckCircle } from 'react-icons/fi';
import { topicsIndex } from '../data/topicsIndex';
import TopicContent from '../components/content/TopicContent';
import CodeExample from '../components/content/CodeExample';
import CodePlayground from '../components/content/CodePlayground';
import CodeExplanation from '../components/content/CodeExplanation';
import VisualDiagram from '../components/content/VisualDiagram';
import Quiz from '../components/content/Quiz';

const Topic = ({ user }) => {
  const { language, category, topicId } = useParams();
  const [topicData, setTopicData] = useState(null);
  const [isBookmarked, setIsBookmarked] = useState(false);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const loadTopic = async () => {
      try {
        // Load topic data
        const topicModule = await import(`../data/topics/${language}/${category}/${topicId}.js`);
        setTopicData(topicModule.default);

        // Check if bookmarked
        if (user) {
          const userDoc = await getDoc(doc(db, 'users', user.uid));
          if (userDoc.exists()) {
            const bookmarks = userDoc.data().bookmarkedTopics || [];
            setIsBookmarked(bookmarks.includes(`${language}/${category}/${topicId}`));
          }
        }
      } catch (error) {
        console.error('Error loading topic:', error);
      } finally {
        setLoading(false);
      }
    };

    loadTopic();
  }, [language, category, topicId, user]);

  const handleBookmark = async () => {
    if (isDemoMode) {
      alert('🎮 Demo Mode: Bookmark feature requires Firebase setup. See SETUP_GUIDE.md');
      return;
    }
    
    if (!user) {
      alert('Please login to bookmark topics');
      return;
    }

    try {
      const userRef = doc(db, 'users', user.uid);
      const topicPath = `${language}/${category}/${topicId}`;

      if (isBookmarked) {
        // Remove bookmark
        const userDoc = await getDoc(userRef);
        const bookmarks = userDoc.data().bookmarkedTopics || [];
        await updateDoc(userRef, {
          bookmarkedTopics: bookmarks.filter(b => b !== topicPath)
        });
        setIsBookmarked(false);
      } else {
        // Add bookmark
        await setDoc(userRef, {
          bookmarkedTopics: arrayUnion(topicPath)
        }, { merge: true });
        setIsBookmarked(true);
      }
    } catch (error) {
      console.error('Error toggling bookmark:', error);
    }
  };

  const markAsComplete = async () => {
    if (isDemoMode) {
      alert('🎮 Demo Mode: Progress tracking requires Firebase setup. See SETUP_GUIDE.md');
      return;
    }
    
    if (!user) {
      alert('Please login to track progress');
      return;
    }

    try {
      const userRef = doc(db, 'users', user.uid);
      const topicPath = `${language}/${category}/${topicId}`;
      
      await setDoc(userRef, {
        completedTopics: arrayUnion(topicPath)
      }, { merge: true });
      
      alert('Topic marked as complete! 🎉');
    } catch (error) {
      console.error('Error marking complete:', error);
    }
  };

  if (loading) {
    return <div className="loading-screen"><div className="spinner"></div></div>;
  }

  if (!topicData) {
    return <div className="content-section"><h2>Topic not found</h2></div>;
  }

  return (
    <div>
      <div className="topic-breadcrumb">
        <span>{language.toUpperCase()}</span>
        <FiChevronRight />
        <span>{category}</span>
        <FiChevronRight />
        <span>{topicData.title}</span>
      </div>

      <div className="topic-header">
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start' }}>
          <div>
            <h1 className="topic-title">{topicData.title}</h1>
            <p className="topic-description">{topicData.description}</p>
          </div>
          <button 
            className={`bookmark-btn ${isBookmarked ? 'bookmarked' : ''}`}
            onClick={handleBookmark}
          >
            <FiBookmark />
          </button>
        </div>
      </div>

      <TopicContent content={topicData} />

      {topicData.diagram && (
        <VisualDiagram 
          type={topicData.diagram.type}
          data={topicData.diagram.data}
          title={topicData.diagram.title}
        />
      )}

      {topicData.codeExplanation && (
        <CodeExplanation 
          title={topicData.codeExplanation.title}
          steps={topicData.codeExplanation.steps}
        />
      )}

      {topicData.examples && topicData.examples.map((example, index) => (
        <CodeExample key={index} example={example} />
      ))}

      {topicData.analogy && (
        <div className="analogy-card">
          <h3 className="analogy-title">{topicData.analogy.title}</h3>
          <div className="analogy-content">{topicData.analogy.content}</div>
        </div>
      )}

      {topicData.playground && (
        <CodePlayground 
          initialCode={topicData.playground.initialCode}
          title={topicData.playground.title}
        />
      )}

      {topicData.quiz && (
        <Quiz 
          questions={topicData.quiz}
          topicId={`${language}-${category}-${topicId}`}
          userId={user?.uid}
        />
      )}

      <div className="content-section">
        <button className="btn btn-primary" onClick={markAsComplete}>
          <FiCheckCircle /> Mark as Complete
        </button>
      </div>
    </div>
  );
};

export default Topic;

